import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { Button, Modal, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import React, { useState } from 'react';
import styled from 'styled-components';
import type { LectureRow } from './StandaloneProgressTable';
import { errorMessage } from './subscriptions.shared';

type Props = {
	lecture: LectureRow;
	studentId: number;
	onClose: (changed: boolean) => void;
};

/** Per-lecture expiry for this student (the lecture roster's own route). */
const LectureExpiryModal: React.FC<Props> = ({ lecture, studentId, onClose }) => {
	const { displaySnackbar } = useSnackbar();
	const [value, setValue] = useState<Date | null>(
		lecture.expire_at ? new Date(lecture.expire_at) : null
	);
	const [saving, setSaving] = useState(false);

	const save = async () => {
		if (!value) return;
		setSaving(true);
		try {
			await api.post('standalone-courses/lectures/enrollment/update', {
				lecture_id: lecture.id,
				user_ids: [studentId],
				expiration_date: value,
			});
			displaySnackbar('success', 'Lecture expiry updated successfully');
			onClose(true);
		} catch (error: any) {
			displaySnackbar('error', errorMessage(error, 'Unable to update lecture expiry'));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal open onClose={() => onClose(false)}>
			<Container>
				<Header>Choose a new expiry date for &quot;{lecture.name}&quot;</Header>
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						value={value}
						onChange={(newValue: any) => setValue(newValue)}
						renderInput={(params: any) => <TextField {...params} />}
					/>
				</LocalizationProvider>
				<Actions>
					<Button variant="text" color="warning" onClick={() => onClose(false)}>
						Cancel
					</Button>
					<Button variant="text" disabled={!value || saving} onClick={save}>
						Confirm
					</Button>
				</Actions>
			</Container>
		</Modal>
	);
};

const Container = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: min(22.563rem, calc(100vw - 2rem));
	border-radius: 4px;
	background-color: #ffffff;
	padding: 30px 30px 10px;
	display: flex;
	flex-direction: column;
	gap: 2rem;
`;
const Header = styled.p`
	font-size: 0.875rem;
	color: #251b33;
`;
const Actions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 1rem;
	padding-bottom: 0.5rem;
`;

export default LectureExpiryModal;
